/**
 * Design-system buttons. Thin typed wrappers over the `.mk-btn-*`
 * classes in tailwind-theme.css — the classes stay the single source of
 * truth for the visual recipe; these components add a typed `variant`
 * (a typo'd variant is a compile error instead of a silently unstyled
 * button) and a safe `type` default.
 *
 *   <Button onClick={save}>Save</Button>
 *   <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
 *   <Button variant="danger" disabled={busy} onClick={remove}>Delete</Button>
 *   <Button type="submit">Sign in</Button>            // inside forms
 *   <ButtonLink to="/dashboard/settings/billing">Upgrade</ButtonLink>
 *
 * `type` defaults to "button" (the native default is "submit", which
 * fires form submission from any modal button that happens to sit in a
 * form) — pass type="submit" explicitly on real submit buttons.
 *
 * Extra Tailwind utilities still compose via className
 * (`<Button className="w-full" …>`), same as with the raw classes.
 */
import type { ComponentProps } from 'react';
import { Link, type LinkProps } from 'react-router-dom';

export type ButtonVariant = 'primary' | 'ghost' | 'danger';

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: 'mk-btn-primary',
  ghost: 'mk-btn-ghost',
  danger: 'mk-btn-danger',
};

// ComponentProps<'button'> (not ButtonHTMLAttributes) so `ref` passes
// through — React 19 ref-as-prop; ConfirmDialog uses it for focus.
export function Button({
  variant = 'primary',
  type = 'button',
  className = '',
  ...rest
}: ComponentProps<'button'> & { variant?: ButtonVariant }) {
  return <button type={type} className={`${VARIANT_CLASS[variant]} ${className}`} {...rest} />;
}

/** Button-styled react-router `<Link>` — internal navigation. */
export function ButtonLink({
  variant = 'primary',
  className = '',
  ...rest
}: LinkProps & { variant?: ButtonVariant }) {
  return <Link className={`${VARIANT_CLASS[variant]} ${className}`} {...rest} />;
}

/** Button-styled `<a>` — external links / full-page navigations. */
export function ButtonAnchor({
  variant = 'primary',
  className = '',
  ...rest
}: ComponentProps<'a'> & { variant?: ButtonVariant }) {
  return <a className={`${VARIANT_CLASS[variant]} ${className}`} {...rest} />;
}
